<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import { useQueryClient } from '@tanstack/vue-query';
import { useAsyncOperationSubscription } from '@/composables/useAsyncOperationSubscription';
import {
  scopedPendingStaticQueryKey,
  useScopedPendingAsyncOperation,
} from '@/composables/api/useAsyncOperationsApi';

const TRACK_MATCHING_ASYNC_TYPE = 'track_matching';

/**
 * Backstop for the polling fallback. Track-matching jobs are short — historically seconds, never
 * minutes — so anything past this is either a worker stall, a network partition, or a backend
 * registration that never produced a terminal row. Without an upper bound the monitor would stay
 * mounted forever once {@link useScopedPendingAsyncOperation} stops polling (its refetchInterval
 * returns `false` for pending=false), holding `:key="code"` against any future submit on the
 * same track.
 */
const WATCHDOG_TIMEOUT_MS = 5 * 60 * 1000;

const props = defineProps<{
  liveTrackCode: string;
}>();

const emit = defineEmits<{
  done: [liveTrackCode: string];
}>();

const queryClient = useQueryClient();
const normalizedCode = computed(() => props.liveTrackCode.trim().toUpperCase());
/** Operation ids already finalized — guards against double-toasting from Echo + polling race. */
const finalizedOperationIds = new Set<string>();
const operationId = ref<string | null>(null);
const enabled = ref(true);
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;

const { data: pendingData } = useScopedPendingAsyncOperation(
  TRACK_MATCHING_ASYNC_TYPE,
  normalizedCode,
  enabled,
);

function clearWatchdog(): void {
  if (watchdogTimer !== null) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
}

/**
 * Stop polling and unmount without toasting. Used when the operation can't be observed:
 *  - First poll returns `pending=false` with no terminal status (op never registered, or
 *    completed + terminal TTL lapsed before the SPA's first request).
 *  - Watchdog elapses without ever reaching {@link finalize} (worker stall / network partition).
 *
 * Silent on purpose — the user already saw the bulk-save acknowledgment; raising a generic
 * error toast for "we lost track of the matching pass" creates more confusion than the symptom.
 */
function giveUpSilently(): void {
  if (!enabled.value) {
    return;
  }
  clearWatchdog();
  operationId.value = null;
  enabled.value = false;
  emit('done', normalizedCode.value);
}

function finalize(payload: {
  operation_id: string;
  status?: string;
  error_code?: string | null;
}): void {
  if (payload.operation_id === '' || finalizedOperationIds.has(payload.operation_id)) {
    return;
  }
  finalizedOperationIds.add(payload.operation_id);

  if (operationId.value === payload.operation_id) {
    operationId.value = null;
  }

  void queryClient.invalidateQueries({ queryKey: ['trackMatching'] });
  void queryClient.invalidateQueries({
    queryKey: scopedPendingStaticQueryKey(TRACK_MATCHING_ASYNC_TYPE, normalizedCode.value),
  });

  if (payload.status === 'succeeded') {
    toast.success(`Track matching finished — ${normalizedCode.value}`);
  } else if (payload.status === 'failed') {
    toast.error(
      payload.error_code
        ? `Track matching failed — ${normalizedCode.value} (${payload.error_code})`
        : `Track matching failed — ${normalizedCode.value}`,
    );
  }

  clearWatchdog();
  enabled.value = false;
  emit('done', normalizedCode.value);
}

watch(
  () => pendingData.value,
  (row) => {
    if (!row) {
      return;
    }

    if (
      row.pending === true &&
      typeof row.operation_id === 'string' &&
      row.operation_id !== ''
    ) {
      if (!finalizedOperationIds.has(row.operation_id)) {
        operationId.value = row.operation_id;
      }
      return;
    }

    // Backend retains a terminal row for ~30s after complete() so the polling fallback can still
    // surface succeeded|failed if the SPA missed the Echo broadcast. Crucially, this must work even
    // when the op finished before any poll observed pending=true (operationId never got set) —
    // finalizedOperationIds dedups against the Echo path.
    if (
      row.pending === false &&
      typeof row.operation_id === 'string' &&
      row.operation_id !== '' &&
      (row.status === 'succeeded' || row.status === 'failed')
    ) {
      finalize({
        operation_id: row.operation_id,
        status: row.status,
        error_code: row.error_code ?? null,
      });
      return;
    }

    // pending=false without a terminal status: the BE has nothing more to tell us about this
    // scope — the controller's registerScoped() either was never reached for this code (e.g.
    // missing performed_by_user_id, scope suffix rejected) or the op completed and its terminal
    // row plus scope index were evicted before the poll observed them. Either way, no toast is
    // appropriate and there's nothing for the Echo path to deliver. Silent unmount frees the
    // `:key="code"` slot in TrackMatchingPage so a subsequent save on the same track gets a
    // fresh monitor instance.
    if (row.pending === false) {
      giveUpSilently();
    }
  },
  { flush: 'post' },
);

useAsyncOperationSubscription(operationId, {
  onFinished: (payload) => {
    if (payload.type !== TRACK_MATCHING_ASYNC_TYPE) {
      return;
    }
    finalize({
      operation_id: payload.operation_id,
      status: payload.status,
      error_code: payload.error_code ?? null,
    });
  },
});

onMounted(() => {
  watchdogTimer = setTimeout(giveUpSilently, WATCHDOG_TIMEOUT_MS);
});

onBeforeUnmount(() => {
  clearWatchdog();
});
</script>

<template>
  <span hidden aria-hidden="true" />
</template>
